import React from 'react'

import { lazyImport } from '../utils/lazyImport'
import { AppRoutes, LayoutType, type RouteConfig } from '../routes.types'

// Shared components
const LazyDashboardPage = lazyImport(() => import('../../pages/shared/DashboardPage'), 'DashboardPage')
const LazySharedProfile = lazyImport(() => import('../../pages/shared/Profile'), 'ProfilePage')
const LazySharedNotificationSettings = lazyImport(
    () => import('../../pages/shared/NotificationSettings'),
    'NotificationSettingsPage',
)
const LazyHelpDocsPage = React.lazy(() =>
    import('../../pages/shared/HelpDocsPage').then((module) => ({ default: module.HelpDocsPage })),
)
const LazyConnectPage = React.lazy(() =>
    import('../../pages/shared/ConnectPage').then((module) => ({ default: module.ConnectPage })),
)
const LazyUploadPage = React.lazy(() =>
    import('../../pages/shared/UploadPage').then((module) => ({ default: module.UploadPage })),
)
const LazyUploadDetailWrapper = React.lazy(() =>
    import('../../components/common/UploadDetailWrapper').then((module) => ({ default: module.UploadDetailWrapper })),
)
const LazyLearnPage = React.lazy(() =>
    import('../../pages/shared/LearnPage').then((module) => ({ default: module.LearnPage })),
)
const LazyAlertsPage = React.lazy(() =>
    import('../../pages/shared/AlertsPage').then((module) => ({ default: module.AlertsPage })),
)
const LazySurveysPage = React.lazy(() =>
    import('../../pages/shared/SurveysPage').then((module) => ({ default: module.SurveysPage })),
)
const LazyReportsPage = React.lazy(() =>
    import('../../pages/shared/ReportsPage').then((module) => ({ default: module.ReportsPage })),
)
const LazyIntakeRouter = React.lazy(() =>
    import('../../components/common/IntakeRouter').then((module) => ({ default: module.IntakeRouter })),
)

export const sharedRoutes: RouteConfig[] = [
    // Core application routes accessible to all authenticated users
    {
        path: AppRoutes.DASHBOARD,
        component: LazyDashboardPage,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Dashboard',
        description: 'Overview of your performance and team metrics',
        requiredPermission: {
            action: 'view',
            subject: 'Dashboard',
        },
    },
    {
        path: AppRoutes.PROFILE,
        component: LazySharedProfile,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Profile',
        description: 'View and manage your profile information',
        requiredPermission: {
            action: 'view',
            subject: 'Profile',
        },
    },
    {
        path: AppRoutes.NOTIFICATION_SETTINGS,
        component: LazySharedNotificationSettings,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Notification Settings',
        description: 'Manage notification preferences',
        requiredPermission: {
            action: 'edit.self', // Matches employee.edit.self permission for managing own settings
            subject: 'Employee',
        },
    },
    {
        path: AppRoutes.CONNECT,
        component: LazyConnectPage,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Connect',
        description: 'Integration management and configuration',
        requiredPermission: {
            action: 'admin.manage', // Matches integrations.admin.manage permission for viewing integrations
            subject: 'Integrations',
        },
    },
    {
        path: AppRoutes.DOCUMENTATION,
        component: LazyHelpDocsPage,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Help & Documentation',
        description: 'Access help resources and documentation',
        // Documentation should be accessible to all authenticated users
        // No specific permission required for help documentation
    },
    {
        path: AppRoutes.UPLOAD,
        component: LazyUploadPage,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        allowedRoles: ['owner', 'admin', 'hr_admin', 'hr_manager', 'people_manager', 'employee'],
        title: 'Uploads',
        description: 'Upload and manage files and data',
        requiredPermission: {
            action: 'view_jobs', // CASL permission check only - backend will determine access
            subject: 'Hris_import',
        },
    },
    {
        path: AppRoutes.UPLOAD_DETAILS,
        component: LazyUploadDetailWrapper,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        allowedRoles: ['owner', 'admin', 'hr_admin', 'hr_manager', 'people_manager', 'employee'],
        title: 'Upload Details',
        description: 'View upload details, timeline, and processing status',
        requiredPermission: {
            action: 'view_jobs', // CASL permission check only - backend will determine access
            subject: 'Hris_import',
        },
    },
    {
        path: AppRoutes.LEARN,
        component: LazyLearnPage,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Learn',
        description: 'Learning resources and training materials',
        requiredPermission: {
            action: 'view',
            subject: 'Dashboard',
        },
    },
    {
        path: AppRoutes.ALERTS,
        component: LazyAlertsPage,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Alerts',
        description: 'Team alerts and notifications',
        requiredPermission: {
            // Uses analytics.view.team permission which works for both roles:
            // - HR roles (admin, hr_admin, hr_manager) have analytics.view.team + analytics.view.overview
            // - Manager role (people_manager) has analytics.view.team
            // Router component internally shows org-wide alerts for HR, team-scoped alerts for managers
            action: 'view.team',
            subject: 'Analytics',
        },
    },
    {
        path: AppRoutes.SURVEYS,
        component: LazySurveysPage,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Surveys',
        description: 'Team surveys and feedback collection',
        requiredPermission: {
            // Uses surveys.view_responses permission which works for both roles:
            // - HR roles (admin, hr_admin, hr_manager) have surveys.view + surveys.view_responses
            // - Manager role (people_manager) has surveys.view_responses
            // Router component internally shows org-wide surveys for HR, team-scoped surveys for managers
            action: 'view_responses',
            subject: 'Surveys',
        },
    },
    {
        path: AppRoutes.REPORTS,
        component: LazyReportsPage,
        layout: LayoutType.APP,
        requireAuth: true,
        requireOrganization: true,
        title: 'Reports',
        description: 'Generate and view team reports',
        requiredPermission: {
            // Uses analytics.view.team permission which works for both roles:
            // - HR roles (admin, hr_admin, hr_manager) have analytics.view.team + analytics.view.overview
            // - Manager role (people_manager) has analytics.view.team
            // Router component internally shows org-wide reports for HR, team-scoped reports for managers
            action: 'view.team',
            subject: 'Analytics',
        },
    },
    {
        path: AppRoutes.HR_INTAKE, // Using existing HR_INTAKE route which is '/intake'
        component: LazyIntakeRouter,
        layout: LayoutType.PROTECTED_MINIMAL,
        requireAuth: true,
        requireOrganization: true,
        title: 'Intake',
        description: 'Complete intake survey or set up organization',
        requiredPermission: {
            action: 'respond',
            subject: 'Surveys',
        },
    },
]
